import ConverterPageBaseComponent from './converter/converter-page-base.component';
import AVTrimmerComponent from './trimmer/av-trimmer.component';
import FileDownloadComponent from './downloads/file-download.component';
import FAQPageComponent from './faq/faq-pages.component';
import FAQComponent from './faq/faq.component';
import ImageCompressComponent from './image-editor/image-compress.component';
import ImageResizeRootComponent from './image-editor/image-resize-root.component';
import MemeGeneratorComponent from './meme/meme-generator.component';
import AccountVerifyPageComponent from './portal/account/account-verify-page.component';
import PasswordResetPageComponent from './portal/account/password-reset-page.component';
import PortalComponent from './portal/portal.component';
import SinglePageBaseComponent from './single-page-converters/single-page-base.component';
import StaticPageComponent from './static-pages/static-page.component';
import PricePageComponent from './ui/product/price-page.component';
import ProductPageComponent from './ui/product/product-page.component';
import dynamic from 'next/dynamic';

const ToolsComponent = dynamic(() => import('../pages/tools/[tools]'));
const ContactUsComponent = dynamic(() => import('./contact-us/contact-us.component'));
const AVCutterComponent = dynamic(() => import('./cutter/av-cutter.component'));
const HelpUsGrowComponent = dynamic(() => import('./growth/help-us-grow.component'));
const LinkUsComponent = dynamic(() => import('./growth/link-us.component'));


class RouteComponentProvider {
    private routeToComponentMap = {
        'faq/.+': FAQPageComponent, // Order matters since some faq might have word convert or compress

        // Utility tools — exact match, must be BEFORE generic \w+-converter regex
        'color-converter': SinglePageBaseComponent,
        'number-base-converter': SinglePageBaseComponent,
        'text-case-converter': SinglePageBaseComponent,
        'json-yaml-xml-converter': SinglePageBaseComponent,
        'hash-generator': SinglePageBaseComponent,
        'image-to-text': SinglePageBaseComponent,
        'markdown-html-converter': SinglePageBaseComponent,
        'lorem-ipsum-generator': SinglePageBaseComponent,
        // Data format convert routes — exact match, must be BEFORE convert-.+?-to-.+ regex
        'convert-json-to-yaml': SinglePageBaseComponent,
        'convert-yaml-to-json': SinglePageBaseComponent,
        'convert-json-to-xml': SinglePageBaseComponent,
        'convert-xml-to-json': SinglePageBaseComponent,
        'convert-yaml-to-xml': SinglePageBaseComponent,
        'convert-xml-to-yaml': SinglePageBaseComponent,
        'convert-markdown-to-html': SinglePageBaseComponent,
        'convert-html-to-markdown': SinglePageBaseComponent,
        // Phase 2 convert routes — exact match
        'convert-csv-to-json': SinglePageBaseComponent,
        'convert-json-to-csv': SinglePageBaseComponent,
        'convert-timestamp-to-date': SinglePageBaseComponent,
        'convert-date-to-timestamp': SinglePageBaseComponent,
        'timestamp-converter': SinglePageBaseComponent,
        // Phase 2 exact-match (don't match \w+ regex due to hyphens)
        'html-entity-encoder': SinglePageBaseComponent,
        'html-entity-decoder': SinglePageBaseComponent,
        'text-diff': SinglePageBaseComponent,
        'chmod-calculator': SinglePageBaseComponent,
        'aspect-ratio-calculator': SinglePageBaseComponent,
        'qr-code-generator': SinglePageBaseComponent,
        'password-generator': SinglePageBaseComponent,
        'uuid-generator': SinglePageBaseComponent,
        'cron-generator': SinglePageBaseComponent,
        // S1 family #3 — SEO / web generators (FEATURE_EXPANSION_RESEARCH.md §5.3)
        'favicon-generator': SinglePageBaseComponent,
        'og-image-generator': SinglePageBaseComponent,
        'twitter-card-generator': SinglePageBaseComponent,
        'meta-tag-generator': SinglePageBaseComponent,
        'json-ld-schema-generator': SinglePageBaseComponent,
        'sitemap-generator': SinglePageBaseComponent,
        'robots-txt-generator': SinglePageBaseComponent,
        'htaccess-generator': SinglePageBaseComponent,
        'image-to-base64': SinglePageBaseComponent,
        // S1 family #4 — JSON → type generators (FEATURE_EXPANSION_RESEARCH.md §5.4)
        'convert-json-to-typescript': SinglePageBaseComponent,
        'convert-json-to-go': SinglePageBaseComponent,
        'convert-json-to-python': SinglePageBaseComponent,
        'convert-json-to-rust': SinglePageBaseComponent,
        'convert-json-to-kotlin': SinglePageBaseComponent,
        'convert-json-to-java': SinglePageBaseComponent,
        'convert-json-to-csharp': SinglePageBaseComponent,
        'convert-json-to-jsonschema': SinglePageBaseComponent,
        // S1 family #4 — HTML table converters
        'convert-html-table-to-markdown': SinglePageBaseComponent,
        'convert-html-table-to-csv': SinglePageBaseComponent,
        'convert-html-table-to-json': SinglePageBaseComponent,
        // S1 family #4 — cURL converters
        'convert-curl-to-fetch': SinglePageBaseComponent,
        'convert-curl-to-httpie': SinglePageBaseComponent,
        'convert-curl-to-python': SinglePageBaseComponent,
        'convert-curl-to-wget': SinglePageBaseComponent,
        // S1 family #4 — dev format formatters
        'toml-formatter': SinglePageBaseComponent,
        'ini-formatter': SinglePageBaseComponent,
        'properties-formatter': SinglePageBaseComponent,
        'env-formatter': SinglePageBaseComponent,
        'hcl-formatter': SinglePageBaseComponent,
        'graphql-formatter': SinglePageBaseComponent,
        // S1 family #4 — utility generators
        'markdown-toc-generator': SinglePageBaseComponent,
        'slug-generator': SinglePageBaseComponent,
        'big-number-calculator': SinglePageBaseComponent,
        'roman-numeral-converter': SinglePageBaseComponent,
        'hex-dump-viewer': SinglePageBaseComponent,
        // S1 family #4 — lookup tables
        'http-status-codes': SinglePageBaseComponent,
        'mime-types': SinglePageBaseComponent,
        'ascii-table': SinglePageBaseComponent,
        'unicode-table': SinglePageBaseComponent,
        // S3 family #5 — calculators (FEATURE_EXPANSION_RESEARCH.md §5.5)
        // Each formula is distinct, so per-slug exact-match entries.
        'bmi-calculator': SinglePageBaseComponent,
        'bmr-calculator': SinglePageBaseComponent,
        'tdee-calculator': SinglePageBaseComponent,
        'body-fat-calculator': SinglePageBaseComponent,
        'calorie-calculator': SinglePageBaseComponent,
        'due-date-calculator': SinglePageBaseComponent,
        'percentage-calculator': SinglePageBaseComponent,
        'fraction-calculator': SinglePageBaseComponent,
        'ratio-calculator': SinglePageBaseComponent,
        'mortgage-calculator': SinglePageBaseComponent,
        'loan-calculator': SinglePageBaseComponent,
        'auto-loan-calculator': SinglePageBaseComponent,
        'refinance-calculator': SinglePageBaseComponent,
        'compound-interest-calculator': SinglePageBaseComponent,
        'simple-interest-calculator': SinglePageBaseComponent,
        'retirement-calculator': SinglePageBaseComponent,
        '401k-calculator': SinglePageBaseComponent,
        'savings-calculator': SinglePageBaseComponent,
        'paycheck-calculator': SinglePageBaseComponent,
        'salary-calculator': SinglePageBaseComponent,
        'gpa-calculator': SinglePageBaseComponent,
        'grade-calculator': SinglePageBaseComponent,
        'time-card-calculator': SinglePageBaseComponent,
        'hours-between-calculator': SinglePageBaseComponent,
        'working-days-calculator': SinglePageBaseComponent,
        'sales-tax-calculator': SinglePageBaseComponent,
        'vat-calculator': SinglePageBaseComponent,
        'gst-calculator': SinglePageBaseComponent,
        'sunrise-sunset-calculator': SinglePageBaseComponent,
        'tip-calculator': SinglePageBaseComponent,
        'discount-calculator': SinglePageBaseComponent,
        'cidr-calculator': SinglePageBaseComponent,
        'subnet-calculator': SinglePageBaseComponent,
        // S3 family #6 (xconvert-web slice) — ISO-8601 validator
        'iso-8601-validator': SinglePageBaseComponent,
        // S2 family #2 — color extras + CSS generators (FEATURE_EXPANSION_RESEARCH.md §5.2)
        'gradient-generator': SinglePageBaseComponent,
        'palette-extractor': SinglePageBaseComponent,
        'contrast-checker': SinglePageBaseComponent,
        'color-blindness-simulator': SinglePageBaseComponent,
        'eyedropper': SinglePageBaseComponent,
        'pantone-converter': SinglePageBaseComponent,
        'tailwind-palette-builder': SinglePageBaseComponent,
        'material-color-tool': SinglePageBaseComponent,
        'box-shadow-generator': SinglePageBaseComponent,
        'clip-path-generator': SinglePageBaseComponent,
        'css-animation-generator': SinglePageBaseComponent,
        'css-grid-generator': SinglePageBaseComponent,
        'flexbox-generator': SinglePageBaseComponent,
        'border-radius-generator': SinglePageBaseComponent,
        'css-filter-generator': SinglePageBaseComponent,
        'css-transform-generator': SinglePageBaseComponent,
        'glassmorphism-generator': SinglePageBaseComponent,
        'neumorphism-generator': SinglePageBaseComponent,
        'triangle-generator': SinglePageBaseComponent,
        // S2 family #7 — random / fake data + crypto utilities (FEATURE_EXPANSION_RESEARCH.md §5.7)
        'nanoid-generator': SinglePageBaseComponent,
        'ulid-generator': SinglePageBaseComponent,
        'ksuid-generator': SinglePageBaseComponent,
        'fake-name-generator': SinglePageBaseComponent,
        'fake-email-generator': SinglePageBaseComponent,
        'fake-address-generator': SinglePageBaseComponent,
        'fake-phone-generator': SinglePageBaseComponent,
        'random-ip': SinglePageBaseComponent,
        'random-string': SinglePageBaseComponent,
        'random-number': SinglePageBaseComponent,
        'random-color': SinglePageBaseComponent,
        'mock-json-generator': SinglePageBaseComponent,
        'hmac-generator': SinglePageBaseComponent,
        'bcrypt-generator': SinglePageBaseComponent,
        'argon2-generator': SinglePageBaseComponent,
        'scrypt-generator': SinglePageBaseComponent,
        'jwt-encoder': SinglePageBaseComponent,
        'x509-decoder': SinglePageBaseComponent,
        'convert-pem-to-der': SinglePageBaseComponent,
        'convert-der-to-pem': SinglePageBaseComponent,

        // PDF Studio (S6) — all 8 verb slugs render PdfStudioRoot via editorMap.
        // Must be BEFORE the generic rotate-\w+ and extract-\w+ regex entries.
        '^(rotate|watermark|add-page-numbers|delete-pages|reorder|n-up|flatten)-pdf$|^extract-pdf-pages$': SinglePageBaseComponent,

        // Client-only format HUBS ({ext}-converter) — MUST be before the generic
        // \w+-converter below (which would otherwise hand them to the server uploader
        // and 406 on servers/resolve). stl/txt/csv/sub are SUBTITLE hubs; the 3D hubs
        // are obj/glb/gltf/3mf/ply — NOT stl (stl-converter is the subtitle hub).
        '^(srt|vtt|ass|ssa|sbv|sub|lrc|smi|ttml|stl|dfxp|txt|csv)-converter$': SinglePageBaseComponent,
        '^(ttf|otf|woff|woff2|eot)-converter$': SinglePageBaseComponent,
        '^(obj|glb|gltf|3mf|ply|step|iges|brep|3dm)-converter$': SinglePageBaseComponent,

        '\\\w+-converter': ConverterPageBaseComponent,
        '\\\w+-compressor': ConverterPageBaseComponent,
        'image-resizer': ImageResizeRootComponent,

        'archive-\\\w+': ConverterPageBaseComponent,
        // S5 family #8 — font standalones (FEATURE_EXPANSION_RESEARCH.md §5.8)
        'font-face-css-generator': SinglePageBaseComponent,
        'font-subsetter': SinglePageBaseComponent,
        // S6 family #10 — PDF page-ops via pdf-lib (FEATURE_EXPANSION_RESEARCH.md §5.10)
        // All 8 verbs share PdfStudio; URL slug picks the default tab.
        'rotate-pdf': SinglePageBaseComponent,
        'watermark-pdf': SinglePageBaseComponent,
        'add-page-numbers-pdf': SinglePageBaseComponent,
        'delete-pages-pdf': SinglePageBaseComponent,
        'extract-pdf-pages': SinglePageBaseComponent,
        'reorder-pdf': SinglePageBaseComponent,
        'n-up-pdf': SinglePageBaseComponent,
        'flatten-pdf': SinglePageBaseComponent,
        // S4 family #1 — subtitle converter matrix (FEATURE_EXPANSION_RESEARCH.md §5.1)
        // S5 family #8 — font matrix (TTF/OTF/WOFF/WOFF2/EOT)
        // S5 family #9 — 3D model matrix (STL/OBJ/GLB/GLTF/PLY/3MF)
        // Each regex resolves the pair to SinglePageBaseComponent so editorMap
        // (populated at module top) can dispatch to the right shared editor.
        'convert-(srt|vtt|ass|ssa|sbv|sub|lrc|smi|ttml|stl|dfxp|txt|csv)-to-(srt|vtt|ass|ssa|sbv|sub|lrc|smi|ttml|stl|dfxp|txt|csv)': SinglePageBaseComponent,
        'convert-(ttf|otf|woff|woff2|eot)-to-(ttf|otf|woff|woff2|eot)': SinglePageBaseComponent,
        'convert-(stl|obj|glb|gltf|ply|3mf|step|iges|brep|3dm)-to-(stl|obj|glb|gltf|ply|3mf)': SinglePageBaseComponent,
        'convert-.+?-to-.+': ConverterPageBaseComponent,
        'merge-\\\w+-to-\\\w+': ConverterPageBaseComponent,
        'compress-\\\w+': ConverterPageBaseComponent,
        'split-\\\w+': ConverterPageBaseComponent,
        'merge-\\\w+': ConverterPageBaseComponent,
        'extract-\\\w+': ConverterPageBaseComponent,
        'resize-\\\w+': ImageResizeRootComponent,
        'crop-\\\w+': ImageResizeRootComponent,
        'edit-\\\w+': ImageResizeRootComponent,
        'flip-\\\w+': ImageResizeRootComponent,
        'rotate-\\\w+': ImageResizeRootComponent,

        // Phase 2 regex patterns
        '\\\w+-formatter': SinglePageBaseComponent,
        '\\\w+-minifier': SinglePageBaseComponent,
        'validate-\\\w+': SinglePageBaseComponent,
        '\\\w+-tester': SinglePageBaseComponent,

        '\\\w+-encoder': SinglePageBaseComponent,
        '\\\w+-decoder': SinglePageBaseComponent,
        '\\\w+-counter': SinglePageBaseComponent,

        '\\\w+-cutter': AVCutterComponent,
        '\\\w+-trimmer': AVTrimmerComponent,
        'cut-\\\w+': AVCutterComponent,
        'trim-\\\w+': AVTrimmerComponent,
        'meme-generator': MemeGeneratorComponent,

        'portal': PortalComponent,
        'product/\\\w+': PricePageComponent,
        'product': ProductPageComponent,

        'tools/\\\w+': ToolsComponent,
        'tools': ToolsComponent,

        'contact-us': ContactUsComponent,


        'faq': FAQComponent,

        'about-us': StaticPageComponent,
        'privacy-policy': StaticPageComponent,
        'how-to-help-xconvert-to-grow': HelpUsGrowComponent,
        'terms-of-service': StaticPageComponent,
        'link-us': LinkUsComponent,

        'compress-jpg-image-online': ImageCompressComponent,

        'password-reset': PasswordResetPageComponent,
        'verify': AccountVerifyPageComponent,

        'downloads': FileDownloadComponent,

        'admin': dynamic(() => import('./admin/admin-home.component').then(m => ({ default: m.AdminHomeComponent }))),
    }

    resolve(route: string) {
        console.log(`Trying to find a match for path: ${route}`);
        const matchedRoute = Object.keys(this.routeToComponentMap)
            .find(it => {
                // console.log(`Trying to regex: ${it}`);
                return new RegExp(it).test(route);
            });
        if (matchedRoute == null) {
            throw Error(`Component not found for given route ${route}`)
        }
        return this.routeToComponentMap[matchedRoute]
    }
}

const routeToComponentMapProvider = new RouteComponentProvider();
export function resolveComponent(route: string) {
    return routeToComponentMapProvider.resolve(route);
}